import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import {
    WizardInitParamsDto,
    WizardInitResponseDto,
    ApplyBookingResponseDto,
    CreateShippingInstructionDto,
    CreateShippingInstructionItemDto,
    CreateShippingInstructionSpeciesDto,
    FreightCondition,
} from '@opb-workspace/shared';

/**
 * Datos específicos por instructivo de embarque (uno por panel del paso 3).
 *
 * `customerReference` + los 7 campos de Antecedentes Financieros vivían a nivel de booking
 * (form global/Step2) hasta feat-ie-wizard-mejoras (2026-08): ahora cada instructivo guarda su
 * propio valor, igual que ya ocurre con consignatario/notificante/ítems.
 */
export interface InstructivoEmbarqueData {
    consigneePartyId?: number;
    consigneeName?: string;
    consigneeAddress?: string;
    notifyPartyId?: number;
    notifyName?: string;
    notifyAddress?: string;
    /** `tblShippingInstructions.CustomerReference` — por instructivo. */
    customerReference?: string;
    typeOfCargoId?: number;
    methodOfPaymentId?: number;
    modeOfSaleId?: number;
    clauseOfSaleId?: number;
    freightCondition?: FreightCondition;
    palletsChep?: boolean;
    palletsChepQuantity?: number;
    items?: CreateShippingInstructionItemDto[];
}

export interface WizardState {
    initParams: WizardInitParamsDto | null;
    voyageData: WizardInitResponseDto | null;
    bookingData: ApplyBookingResponseDto | null;
    formData: Partial<CreateShippingInstructionDto>;
    instructivos: InstructivoEmbarqueData[];
    currentStep: number;
}

const INITIAL_STATE: WizardState = {
    initParams: null,
    voyageData: null,
    bookingData: null,
    formData: {},
    instructivos: [],
    currentStep: 0,
};

@Injectable({ providedIn: 'root' })
export class InstructivoEmbarqueStateService {
    private state$ = new BehaviorSubject<WizardState>(INITIAL_STATE);
  private static readonly DEFAULT_SPECIES_PACKAGE_TYPE_ID = 3; // BOXES
  private static readonly DEFAULT_SPECIES_PACKAGE_TYPE_LABEL = 'BOXES';

    readonly state = this.state$.asObservable();

    get snapshot(): WizardState {
        return this.state$.getValue();
    }

    initSession(params: WizardInitParamsDto): void {
        this.state$.next({ ...INITIAL_STATE, initParams: params });
    }

    setVoyageData(data: WizardInitResponseDto): void {
        const s = this.snapshot;
        this.state$.next({
            ...s,
            voyageData: data,
            formData: {
                ...s.formData,
                voyageId: data.voyageId,
                portOfLoadingId: data.portOfLoadingId,
                portOfDischargeId: data.portOfDischargeId,
                voyageNumber: data.voyageNumber,
                vesselId: data.vesselId,
                vesselName: data.vesselName,
                carrierId: data.carrierId,
                voyageCarrierDetailId: data.voyageCarrierDetailId,
                carrierName: data.carrierName,
                portOfLoadingName: data.portOfLoadingName,
                portOfDischargeName: data.portOfDischargeName,
                voyageCallLoadingId: data.voyageCallLoadingId,
                voyageCallDischargeId: data.voyageCallDischargeId,
                terminalPol: data.terminalPol,
                etd: data.etd,
                eta: data.eta,
                cutOffDocumental: data.cutOffDocumental,
                cutOffFisico: data.cutOffFisico,
                serviceType: data.serviceType,
                transitDays: data.transitDays,
                countryOfFinalDestination: data.countryOfFinalDestination,
            },
        });
    }

    applyBookingData(booking: ApplyBookingResponseDto): void {
        const s = this.snapshot;
        const existing = s.formData;
        const preserveOpbShipper =
            booking.isOpbBooking &&
            existing.bookingId != null &&
            existing.bookingId === booking.bookingId;
        this.state$.next({
            ...s,
            bookingData: booking,
            formData: {
                ...existing,
                bookingId: booking.bookingId,
                bookingNumber: booking.bookingNumber,
                customerId: booking.customerId,
                customerName: booking.customerName,
                customerReference:
                    existing.customerReference ?? booking.customerReference,
                terminalPol: existing.terminalPol ?? booking.terminalPol,
                etd: existing.etd ?? booking.etd,
                cutOffDocumental:
                    existing.cutOffDocumental ?? booking.cutOffDocumental,
                cutOffFisico: existing.cutOffFisico ?? booking.cutOffFisico,
                billOfLadingIssuer:
                    existing.billOfLadingIssuer ?? booking.billOfLadingIssuer,
                countryOfFinalDestination:
                    existing.countryOfFinalDestination ??
                    booking.countryOfFinalDestination,
                remarks: existing.remarks ?? booking.remarks,
                freightCondition:
                    existing.freightCondition ?? booking.freightCondition,
                customerContactEmail:
                    booking.customerContactEmail ?? existing.customerContactEmail,
                customerAttentionContactName:
                    booking.customerAttentionContactName ??
                    existing.customerAttentionContactName,
                customerTaxIdentificationNumber:
                    booking.customerTaxIdentificationNumber ??
                    existing.customerTaxIdentificationNumber,
                /**
                 * Booking OPB: el paso 2 fija exportador por defecto = parte cliente (`customerId`);
                 * no precargar shipper del SP hasta que el front lo resuelva.
                 */
                ...(booking.isOpbBooking
                    ? {
                          shipperPartyId: preserveOpbShipper
                              ? existing.shipperPartyId
                              : undefined,
                          shipperName: preserveOpbShipper
                              ? existing.shipperName
                              : undefined,
                          shipperAddress: preserveOpbShipper
                              ? existing.shipperAddress
                              : undefined,
                          shipperEmail: preserveOpbShipper
                              ? existing.shipperEmail
                              : undefined,
                          shipperTaxIdentificationNumber: preserveOpbShipper
                              ? existing.shipperTaxIdentificationNumber
                              : undefined,
                          shipperContactName: preserveOpbShipper
                              ? existing.shipperContactName
                              : undefined,
                          shipperPhoneNumber: preserveOpbShipper
                              ? existing.shipperPhoneNumber
                              : undefined,
                          shipperMobilePhoneNumber: preserveOpbShipper
                              ? existing.shipperMobilePhoneNumber
                              : undefined,
                      }
                    : {
                          shipperPartyId:
                              existing.shipperPartyId ??
                              booking.shipper?.partyId,
                          shipperName:
                              existing.shipperName ??
                              booking.shipper?.name ??
                              '',
                          shipperAddress:
                              existing.shipperAddress ??
                              booking.shipper?.address,
                          shipperEmail:
                              booking.shipper?.email ?? existing.shipperEmail,
                          shipperTaxIdentificationNumber:
                              booking.shipper?.taxIdentificationNumber ??
                              existing.shipperTaxIdentificationNumber,
                          shipperContactName:
                              booking.shipper?.contactName ??
                              existing.shipperContactName,
                          shipperPhoneNumber:
                              booking.shipper?.phoneNumber ??
                              existing.shipperPhoneNumber,
                          shipperMobilePhoneNumber:
                              booking.shipper?.mobilePhoneNumber ??
                              existing.shipperMobilePhoneNumber,
                      }),
                items: (() => {
                    const cargoFromBooking = (booking.items ?? []).map(
                        (bi, idx) => ({
                            itemNumber: idx + 1,
                            bookingContainerId: bi.bookingContainerId,
                            quantityOfContainers:
                                bi.quantityOfContainers ?? 1,
                            containerType: bi.containerType,
                            containerTypeId:
                                bi.containerTypeId ??
                                this.inferContainerTypeIdFromLabel(
                                    bi.containerType,
                                ),
                            cargoType: bi.cargoType,
                            coldTreatmentIndicatorId:
                                bi.coldTreatmentIndicatorId,
                            controlledAtmosphereId: bi.controlledAtmosphereId,
                            temperatureCelsius: bi.temperature,
                            temperatureUnit: bi.temperatureUnit ?? 'C',
                            ventilation: bi.ventilation,
                            ventilationUnitId: bi.ventilationUnitId,
                            ventilationUnit: bi.ventilationUnit,
                            atmosphereParams: bi.atmosphereParams,
                            species: (bi.packages ?? []).map((pkg) => ({
                                speciesId: pkg.speciesId,
                                speciesName:
                                    pkg.speciesName ??
                                    pkg.commodityDescription ??
                                    '',
                                varietyId: pkg.varietyId,
                                variety: (pkg.varietyName ?? '').trim(),
                                conditionId: pkg.conditionId,
                                condition: pkg.conditionName ?? '',
                                boxCount: Math.max(
                                    1,
                                    pkg.packageQuantity ?? 1,
                                ),
                                netWeightKgPerBox: 0,
                                grossWeightKgPerBox: 0,
                                typeOfPackageId:
                                    pkg.typeOfPackageId ??
                                    InstructivoEmbarqueStateService.DEFAULT_SPECIES_PACKAGE_TYPE_ID,
                                packageType:
                                    (pkg.packageType ?? '').trim() ||
                                    InstructivoEmbarqueStateService.DEFAULT_SPECIES_PACKAGE_TYPE_LABEL,
                            })),
                        }),
                    ) as CreateShippingInstructionItemDto[];
                    if (
                        booking.isOpbBooking === true &&
                        cargoFromBooking.length > 0
                    ) {
                        return InstructivoEmbarqueStateService.mergeOpbCargoItems(cargoFromBooking);
                    }
                    if (existing.items?.length) {
                        return existing.items;
                    }
                    return cargoFromBooking;
                })(),
            },
        });
    }

    /**
     * Quita el resultado de applyBooking OPB del snapshot (booking externo o edición del campo).
     */
    clearBookingApplySnapshot(): void {
        const s = this.snapshot;
        const fd = { ...s.formData } as Record<string, unknown>;
        delete fd['bookingId'];
        delete fd['customerContactEmail'];
        delete fd['customerAttentionContactName'];
        delete fd['customerTaxIdentificationNumber'];
        this.state$.next({
            ...s,
            bookingData: null,
            formData: fd as Partial<CreateShippingInstructionDto>,
        });
    }

    patchFormData(patch: Partial<CreateShippingInstructionDto>): void {
        const s = this.snapshot;
        this.state$.next({ ...s, formData: { ...s.formData, ...patch } });
    }

    patchInstructivos(instructivos: InstructivoEmbarqueData[]): void {
        this.state$.next({ ...this.snapshot, instructivos });
    }

    /**
     * Completa RUT/email/teléfonos en `formData` desde `bookingData` si faltan,
     * para que el paso 4 y el POST de creación usen los mismos datos que devolvió apply-booking.
     */
    mergePartyContactsFromBookingSnapshot(): void {
        const bd = this.snapshot.bookingData;
        if (!bd) {
            return;
        }
        const fd = this.snapshot.formData;
        const patch: Partial<CreateShippingInstructionDto> = {};
        const empty = (s?: string | null): boolean =>
            s == null || String(s).trim().length === 0;

        const fill = (
            cur: string | undefined | null,
            next: string | undefined | null,
        ): string | undefined => {
            if (!empty(cur) || empty(next)) {
                return undefined;
            }
            return String(next).trim();
        };

        const ship = bd.shipper;
        if (ship) {
            const e = fill(fd.shipperEmail, ship.email);
            if (e !== undefined) patch.shipperEmail = e;
            const t = fill(fd.shipperTaxIdentificationNumber, ship.taxIdentificationNumber);
            if (t !== undefined) patch.shipperTaxIdentificationNumber = t;
            const c = fill(fd.shipperContactName, ship.contactName);
            if (c !== undefined) patch.shipperContactName = c;
            const ph = fill(fd.shipperPhoneNumber, ship.phoneNumber);
            if (ph !== undefined) patch.shipperPhoneNumber = ph;
            const m = fill(fd.shipperMobilePhoneNumber, ship.mobilePhoneNumber);
            if (m !== undefined) patch.shipperMobilePhoneNumber = m;
        }
        if (Object.keys(patch).length > 0) {
            this.patchFormData(patch);
        }
    }

    updateFormData(patch: Partial<CreateShippingInstructionDto>): void {
        const s = this.snapshot;
        this.state$.next({ ...s, formData: { ...s.formData, ...patch } });
    }

    setStep(step: number): void {
        this.state$.next({ ...this.snapshot, currentStep: step });
    }

    reset(): void {
        this.state$.next(INITIAL_STATE);
    }

    private static itemMergeKey(item: CreateShippingInstructionItemDto): string {
        return [
            // NO incluir `bookingContainerId` acá (revertido 2026-08-17, ver lección #103).
            // El commit `be84c52c3` (2026-08-11) lo agregó para que dos contenedores físicos
            // distintos nunca se fusionaran en un ítem, porque bajo el modelo de ESE momento
            // (disponibilidad por identidad de `BookingContainerId`) el contenedor "de más" de
            // un ítem fusionado quedaba sin rastro y `sp_InstructivoEmbarque_ApplyBooking` lo
            // seguía mostrando "pendiente" para siempre (bug real: booking 070ISA1237108). Un día
            // después el cliente aclaró que el modelo correcto es cantidad ACUMULADA (sumar
            // `QuantityOfContainers` de los ítems/instructivos activos contra el total del
            // booking, lección #100/#101) — ya así en `sp_InstructivoEmbarque_ApplyBooking` V5 y
            // en `SP_Insert` (2026-08). Bajo ese modelo, incluir `bookingContainerId` en la clave
            // dejó de evitar un bug y pasó a CAUSAR uno nuevo: el Paso 3 proponía 1 ítem por
            // contenedor físico en vez de 1 bloque con `quantityOfContainers = N` (reporte
            // cliente 2026-08-17). Contenedores con specs idénticas vuelven a fusionarse en 1
            // ítem con cantidad acumulada; el ítem fusionado conserva un único
            // `bookingContainerId` de referencia (el del primer contenedor del grupo) — ya no se
            // usa para calcular disponibilidad, solo queda como dato informativo.
            item.containerTypeId ?? '',
            (item.containerType ?? '').trim().toUpperCase(),
            (item.cargoType ?? '').trim().toUpperCase(),
            item.coldTreatmentIndicatorId ?? '',
            item.controlledAtmosphereId ?? '',
            item.temperatureCelsius ?? '',
            (item.temperatureUnit ?? 'C').trim().toUpperCase(),
            item.ventilationUnitId ?? '',
            (item.atmosphereParams ?? '').trim(),
        ].join('\x00');
    }

    private static mergeOpbCargoItems(
        items: CreateShippingInstructionItemDto[],
    ): CreateShippingInstructionItemDto[] {
        if (items.length <= 1) {
            return items;
        }
        const order: string[] = [];
        const groups = new Map<string, CreateShippingInstructionItemDto>();
        for (const item of items) {
            const key = InstructivoEmbarqueStateService.itemMergeKey(item);
            const acc = groups.get(key);
            if (!acc) {
                order.push(key);
                groups.set(key, { ...item, species: [...(item.species ?? [])] });
            } else {
                acc.quantityOfContainers =
                    (acc.quantityOfContainers ?? 1) + (item.quantityOfContainers ?? 1);
                acc.species = InstructivoEmbarqueStateService.mergeSpeciesRows(
                    acc.species ?? [],
                    item.species ?? [],
                );
            }
        }
        return order.map((key, idx) => ({
            ...groups.get(key)!,
            itemNumber: idx + 1,
        }));
    }

    private static mergeSpeciesRows(
        base: CreateShippingInstructionSpeciesDto[],
        incoming: CreateShippingInstructionSpeciesDto[],
    ): CreateShippingInstructionSpeciesDto[] {
        const result = base.map((s) => ({ ...s }));
        for (const sp of incoming) {
            const key = `${sp.speciesId ?? ''}\x00${sp.conditionId ?? ''}\x00${sp.typeOfPackageId ?? ''}`;
            const match = result.find(
                (r) =>
                    `${r.speciesId ?? ''}\x00${r.conditionId ?? ''}\x00${r.typeOfPackageId ?? ''}` ===
                    key,
            );
            if (match) {
                match.boxCount = (match.boxCount ?? 0) + (sp.boxCount ?? 0);
            } else {
                result.push({ ...sp });
            }
        }
        return result;
    }

  private inferContainerTypeIdFromLabel(
    containerType: string | undefined,
  ): number | undefined {
    if (!containerType) {
      return undefined;
    }
    const normalized = containerType
      .trim()
      .toUpperCase()
      .replace(/[\s\-_/]+/g, '');
    if (normalized.includes('REEFER40') || normalized.includes('40RHC')) {
      return 5;
    }
    if (normalized.includes('REEFER20') || normalized.includes('20RHC')) {
      return 4;
    }
    if (normalized.includes('40')) {
      return 2;
    }
    if (normalized.includes('20')) {
      return 1;
    }
    if (normalized.includes('PALLET')) {
      return 6;
    }
    return undefined;
  }
}
